'use client'

import { Icon } from "@/components/ui/icon"
import { acceptTermsOfService } from "@/lib/auth/actions/user-tos"
import { Modal, Checkbox } from "@heroui/react"
import { Button } from "@/components/ui/button"
import { useSession } from "next-auth/react"
import { useRouter } from "next/navigation"
import { useState } from "react"

interface TermsOfServiceModalProps {
  isOpen: boolean
  onClose: () => void
  userId: string
  version?: string
  onAccepted?: () => void
}

export function TermsOfServiceModal({
  isOpen,
  onClose,
  userId,
  version = "1.0",
  onAccepted
}: TermsOfServiceModalProps) {
  const router = useRouter()
  const { update: updateSession } = useSession()
  const [hasAgreed, setHasAgreed] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleAccept = async () => {
    if (!hasAgreed) {
      setError("You must agree to the Terms of Service to continue")
      return
    }

    try {
      setIsSubmitting(true)
      setError(null)

      const result = await acceptTermsOfService({
        userId,
        accepted: true,
        version
      })

      if (!result.success) {
        setError(result.error || "Failed to accept terms of service")
        return
      }

      // Update the session to reflect the accepted ToS
      await updateSession({
        tosAccepted: true,
        tosVersion: version,
        tosAcceptedAt: new Date().toISOString(),
        setupSteps: ['terms_of_service'],
        isSetupComplete: true
      })

      // Terms accepted successfully
      if (onAccepted) {
        onAccepted()
      }

      // Close modal and refresh page
      onClose()
      router.refresh()
    } catch (error) {
      setError("An unexpected error occurred")
      console.error("ToS acceptance error:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDecline = () => {
    // redirect to privacy policy
    router.push('/privacy-policy')
  }

  return (
    <Modal.Backdrop
      isOpen={isOpen}
      isDismissable={false}
      isKeyboardDismissDisabled={true}
    >
      <Modal.Container size="lg">
        <Modal.Dialog className="bg-content1">
          <Modal.Header className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Icon icon="file-text" className="text-primary" />
            <h2 className="text-xl font-semibold">Terms of Service</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Please review and accept our Terms of Service to continue
          </p>
          {version !== "1.0" && (
            <p className="text-xs text-primary">
              Our Terms of Service have been updated (v{version})
            </p>
          )}
        </Modal.Header>
        <Modal.Body className="max-h-[60vh] overflow-y-auto">
          <div className="space-y-4">
            <section>
              <h3 className="text-lg font-medium mb-2">1. Terms of Use</h3>
              <p>
                By accessing and using this platform, you agree to comply with and be bound by these terms and conditions.
                Choice Graphics provides a platform for the community to find and share vehicle graphics design, installation, and management services.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-medium mb-2">2. User Responsibilities</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Maintain accurate account information</li>
                <li>Comply with all applicable laws and regulations</li>
                <li>Respect other users and their content</li>
                <li>Ensure submitted designs comply with legal standards</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-medium mb-2">3. Content Guidelines</h3>
              <p>
                Users are responsible for the content they post and share on the platform.
                All content must comply with our community guidelines and legal standards.
                Choice Graphics reserves the right to remove content that violates these guidelines.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-medium mb-2">4. Privacy & Data</h3>
              <p>
                We collect and process your personal data as described in our Privacy Policy.
                By using our services, you consent to such processing and you warrant that all
                data provided by you is accurate and complete.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-medium mb-2">5. Intellectual Property</h3>
              <p>
                Users retain ownership of their original content. By uploading content to our platform,
                you grant Choice Graphics a license to use, reproduce, and display such content solely
                for the purpose of providing our services.
              </p>
            </section>
          </div>
        </Modal.Body>
        <Modal.Footer className="flex flex-col items-stretch gap-4">
          <Checkbox
            isSelected={hasAgreed}
            onChange={setHasAgreed}
            size="sm"
          >
            <Checkbox.Content>
              <Checkbox.Control>
                <Checkbox.Indicator />
              </Checkbox.Control>
              I have read and agree to the Terms of Service
            </Checkbox.Content>
          </Checkbox>

          {error && (
            <p className="text-danger text-sm">{error}</p>
          )}

          <div className="flex justify-between gap-2">
            <Button
              variant="outline"
              onPress={handleDecline}
              isDisabled={isSubmitting}
            >
              Decline
            </Button>
            <Button
              onPress={handleAccept}
              isLoading={isSubmitting}
              isDisabled={!hasAgreed || isSubmitting}
            >
              Accept & Continue
            </Button>
          </div>
        </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  )
}
